import React, { useState } from "react";
import PropTypes from "prop-types";
import GridContainer from "components/Grid/GridContainer";
import GridItem from "components/Grid/GridItem";
import Button from "components/CustomButtons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import { ArrowBack, ArrowForward } from "@material-ui/icons";
import { formatNumber } from "components/Utils/utils.js";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles((theme) => ({
  font700: {
    fontWeight: 700,
  },
  black: {
    color: "#000",
  },
  green: {
    color: "#13816F",
    fontWeight: "bold",
  },
  paginatorContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginTop: "20px",
  },
  paginatorButton: {
    color: theme.palette.primary.main,
  },
  paginatorInfo: {
    margin: "0 10px",
    fontWeight: 500,
  },
  badge: {
    display: "inline-block",
    padding: "4px 10px",
    fontSize: "12px",
    fontWeight: "bold",
    lineHeight: "1",
    textTransform: "uppercase",
    borderRadius: "12px",
    color: "#fff",
    backgroundColor: theme.palette.primary.main, // Color principal
    whiteSpace: "nowrap",
  },
  success: {
    backgroundColor: theme.palette.success.main,
  },
  danger: {
    backgroundColor: theme.palette.error.main,
  },
  warning: {
    backgroundColor: theme.palette.warning.main,
  },
  info: {
    backgroundColor: theme.palette.info.main,
  },
  gray: {
    backgroundColor: theme.palette.grey[500],
  },
}));

const PolicyCard = ({
  order,
  classes,
  history,
  server,
  calculateExpirationText,
  getMakeModelName,
  quoteType,
}) => {
  const { t } = useTranslation();
  const status = calculateExpirationText(order.expiration_date);

  return (
    <>
      <GridItem xs={12} style={{ marginLeft: "20px" }}>
        <GridContainer style={{ marginBottom: "20px" }}>
          <GridItem xs={6}>
            <div className={`${classes.font700} ${classes.black}`}>
              {t("home.policyNumber")}
            </div>
          </GridItem>
          <GridItem xs={6}>
            {order.policy_number ? (
              <a href={`/guest/summary/${order.id}`}>
                {order.policy_number || "N/A"}
              </a>
            ) : (
              "N/A"
            )}
          </GridItem>
        </GridContainer>

        <GridContainer style={{ marginBottom: "20px" }}>
          <GridItem xs={6}>
            <div className={`${classes.font700} ${classes.black}`}>
              {t("home.expirationDate")}
            </div>
          </GridItem>
          <GridItem xs={6}>
            <span>{order.expiration_date || "N/A"}</span>
          </GridItem>
        </GridContainer>

        <GridContainer style={{ marginBottom: "20px" }}>
          <GridItem xs={6}>
            <div className={`${classes.font700} ${classes.black}`}>
              {t("home.clientName")}
            </div>
          </GridItem>
          <GridItem xs={6}>{order.customer_name || "N/A"}</GridItem>
        </GridContainer>

        <GridContainer style={{ marginBottom: "20px" }}>
          <GridItem xs={6}>
            <div className={`${classes.font700} ${classes.black}`}>
              {t("home.term")}
            </div>
          </GridItem>
          <GridItem xs={6}>{order.policy_type || "N/A"}</GridItem>
        </GridContainer>
      </GridItem>
    </>
  );
};

PolicyCard.propTypes = {
  order: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  server: PropTypes.string.isRequired,
  calculateExpirationText: PropTypes.func.isRequired,
};

const PaginatedPoliciesMobileAgentHome = ({
  orders,
  calculateExpirationText,
  history,
  quoteType,
  server,
  getMakeModelName,
}) => {
  const classes = useStyles();
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 1;
  const totalPages = Math.ceil(orders.length / itemsPerPage);

  const handlePageChange = (direction) => {
    if (direction === "prev" && currentPage > 0) {
      setCurrentPage(currentPage - 1);
    } else if (direction === "next" && currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const paginatedOrders = orders.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  return (
    <>
      <GridContainer>
        {paginatedOrders.map((order) => (
          <PolicyCard
            key={order.policy_nbr || order.order_id}
            order={order}
            quoteType={quoteType}
            classes={classes}
            history={history}
            server={server}
            getMakeModelName={getMakeModelName}
            calculateExpirationText={calculateExpirationText}
          />
        ))}
      </GridContainer>
      <div className={classes.paginatorContainer}>
        <IconButton
          className={classes.paginatorButton}
          disabled={currentPage === 0}
          onClick={() => handlePageChange("prev")}
        >
          <ArrowBack />
        </IconButton>
        <Typography className={classes.paginatorInfo}>
          {currentPage + 1} of {totalPages}
        </Typography>
        <IconButton
          className={classes.paginatorButton}
          disabled={currentPage === totalPages - 1}
          onClick={() => handlePageChange("next")}
        >
          <ArrowForward />
        </IconButton>
      </div>
    </>
  );
};

PaginatedPoliciesMobileAgentHome.propTypes = {
  orders: PropTypes.array.isRequired,
  calculateExpirationText: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  server: PropTypes.string.isRequired,
  getMakeModelName: PropTypes.string.isRequired,
};

export default PaginatedPoliciesMobileAgentHome;
